import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check, Copy, KeyRound, Loader2, Plus, ShieldCheck, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardIcon, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { useNetwork } from '@/contexts/NetworkContext'
import { useCurrentUser } from '@/contexts/UserContext'
import { isSuperAdmin } from '@/data/superAdmins'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription, ApiError } from '@/services/core/apiError'
import { RolesApi, type RoleDetail, type RoleInput } from '@/services/users/rolesApi'
import {
  availablePermissions,
  cleanPermissions,
  hasRoleTypes,
  roleTypeOf,
  type RoleScope,
  type RoleType,
} from '@/services/users/rolePermissions'
import { networkApps } from '@/services/users/userRoles'
import { AppShell } from '../../components/AppShell'
import { CopyRoleDialog } from '../../components/CopyRoleDialog'
import { FieldLabel } from '../../components/FieldLabel'
import { FormSkeleton } from '../../components/FormSkeleton'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { RoleUsersCard } from '../../components/RoleUsersCard'
import { StatsBar, StatsBarActivity, StatsBarId } from '../../components/StatsBar'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { formatDate } from '../../utils/format'
import {
  ObjectStateToggle,
  useSaveObjectState,
  type ObjectState,
} from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

const SCOPES: RoleScope[] = ['NETWORK', 'TEAM']
const ROLE_TYPES: RoleType[] = ['INTERNAL', 'EXTERNAL']

/** A row of radio buttons for one of a handful of values. */
function RadioRow<T extends string>({
  value,
  options,
  label,
  onChange,
}: {
  value: T
  options: T[]
  label: (option: T) => string
  onChange: (value: T) => void
}) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(v) => onChange(v as T)}
      className="flex flex-wrap gap-4"
    >
      {options.map((option) => (
        <Label key={option} className="font-normal">
          <RadioGroupItem value={option} />
          {label(option)}
        </Label>
      ))}
    </RadioGroup>
  )
}

// ── Inner form component ────────────────────────────────────────────────────

interface RoleEditFormProps {
  role: RoleDetail | null
  id: string
  isNew: boolean
}

function RoleEditForm({ role, id, isNew }: RoleEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentNetwork } = useNetwork()
  const currentUser = useCurrentUser()
  const superAdmin = isSuperAdmin(currentUser?.username)
  const [copyOpen, setCopyOpen] = useState(false)

  const apps = useMemo(() => {
    const offered = networkApps(currentNetwork)
    // Keep a saved role's application even if the network no longer runs it.
    return role?.application && !offered.includes(role.application)
      ? [...offered, role.application]
      : offered
  }, [currentNetwork, role?.application])
  const roleTypes = hasRoleTypes(currentNetwork)

  const form = useForm({
    defaultValues: {
      name: role?.name ?? '',
      description: role?.description ?? '',
      application: role?.application || apps[0] || '',
      roleType: roleTypeOf(role?.application ?? '', role?.permissions ?? []),
      scope: (role?.scope || 'NETWORK') as RoleScope,
      permissions: role?.permissions ?? [],
    },
    onSubmit: ({ value }) => {
      const shape = {
        application: value.application,
        scope: value.scope,
        roleType: effectiveRoleType(value.application, value.roleType),
      }
      mutation.mutate({
        name: value.name,
        description: value.description,
        application: value.application,
        scope: value.scope,
        permissions: cleanPermissions(value.permissions, shape),
        objectState: (role?.objectState ?? 'ACTIVE') as ObjectState,
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => RolesApi.update(id, { name: role?.name ?? '', objectState }),
    ['role', id],
    ['roles']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  /** Only advertising roles on a self-service network can be external. */
  const effectiveRoleType = (application: string, roleType: RoleType): RoleType =>
    application === 'ADVERTISING' && roleTypes ? roleType : 'INTERNAL'

  const mutation = useMutation({
    mutationFn: (input: RoleInput) =>
      isNew ? RolesApi.create(id, input) : RolesApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['role', id], data)
      queryClient.invalidateQueries({ queryKey: ['roles'] })
      form.setFieldValue('permissions', data.permissions)
      markClean()
      if (isNew) navigate(`/admin/roles/${id}`, { replace: true })
      toast.success(t('common.saved'), {
        description: isNew ? t('roles.createdSuccess') : t('roles.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError
          ? error.message
          : isNew
            ? t('roles.createFailed')
            : t('roles.saveFailed')
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('roles.title'), href: '/admin/roles' },
    { label: isNew ? t('roles.newRole') : (role?.name ?? t('roles.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={role?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.role')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('roles.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {role && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <Button type="button" variant="outline" onClick={() => setCopyOpen(true)}>
                <Copy className="size-3.5" />
                {t('copyRole.copy')}
              </Button>
              <ObjectStateToggle value={role.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {role && !isNew && (
          <StatsBar
            items={[
              { label: t('common.id'), content: <StatsBarId value={role.id} /> },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(role.createTime)}
                    updated={formatDate(role.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={ShieldCheck} />
                  {t('roles.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <FieldLabel>{t('common.description')}</FieldLabel>
                    <form.Field name="description">
                      {(field) => (
                        <Textarea
                          rows={3}
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('roles.descriptionPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                  {apps.length > 1 && (
                    <div className="p-field full">
                      <FieldLabel tooltip={t('roles.applicationHelp')}>
                        {t('roles.application')}
                      </FieldLabel>
                      <form.Field name="application">
                        {(field) => (
                          <RadioRow
                            value={field.state.value}
                            options={apps}
                            label={(app) => t(`userRoles.apps.${app}`, app)}
                            onChange={(v) => field.handleChange(v)}
                          />
                        )}
                      </form.Field>
                    </div>
                  )}
                  <form.Subscribe selector={(s) => s.values.application}>
                    {(application) =>
                      application === 'ADVERTISING' &&
                      roleTypes && (
                        <div className="p-field full">
                          <FieldLabel tooltip={t('roles.roleTypeHelp')}>
                            {t('roles.roleType')}
                          </FieldLabel>
                          <form.Field name="roleType">
                            {(field) => (
                              <RadioRow
                                value={field.state.value}
                                options={ROLE_TYPES}
                                label={(type) => t(`roles.roleTypes.${type}`)}
                                onChange={(v) => field.handleChange(v)}
                              />
                            )}
                          </form.Field>
                        </div>
                      )
                    }
                  </form.Subscribe>
                  <div className="p-field full">
                    <FieldLabel tooltip={t('roles.scopeHelp')}>{t('roles.scope')}</FieldLabel>
                    <form.Field name="scope">
                      {(field) => (
                        <RadioRow
                          value={field.state.value}
                          options={SCOPES}
                          label={(scope) => t(`roles.scopes.${scope}`)}
                          onChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card tone="people">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={KeyRound} />
                  {t('roles.permissions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form.Subscribe
                  selector={(s) => [s.values.application, s.values.roleType, s.values.scope]}
                >
                  {([application, roleType, scope]) => (
                    <form.Field name="permissions">
                      {(field) => {
                        const offered = availablePermissions(
                          currentNetwork,
                          {
                            application,
                            scope: scope as RoleScope,
                            roleType: effectiveRoleType(application, roleType as RoleType),
                          },
                          field.state.value,
                          superAdmin
                        )
                        if (!offered.length)
                          return (
                            <p className="text-xs text-muted-foreground">
                              {t('roles.noPermissions')}
                            </p>
                          )
                        const selected = new Set(field.state.value)
                        return (
                          <ul className="flex flex-col gap-3">
                            {offered.map((key) => (
                              <li key={key} className="flex items-start gap-2">
                                <Checkbox
                                  id={`permission-${key}`}
                                  className="mt-0.5"
                                  checked={selected.has(key)}
                                  onCheckedChange={(checked) =>
                                    field.handleChange(
                                      checked === true
                                        ? [...field.state.value, key]
                                        : field.state.value.filter((p) => p !== key)
                                    )
                                  }
                                />
                                <Label
                                  htmlFor={`permission-${key}`}
                                  className="flex flex-col items-start gap-0.5 font-normal"
                                >
                                  <span className="font-medium">
                                    {t(`roles.permissionNames.${key}.name`, key)}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {t(`roles.permissionNames.${key}.desc`, '')}
                                  </span>
                                </Label>
                              </li>
                            ))}
                          </ul>
                        )
                      }}
                    </form.Field>
                  )}
                </form.Subscribe>
              </CardContent>
            </Card>
          </div>
          {role && !isNew && (
            <div className="flex min-w-0 flex-1 flex-col gap-3.5">
              <RoleUsersCard roleId={id} application={role.application} />
            </div>
          )}
        </div>

        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/roles')}
              disabled={mutation.isPending}
            >
              <X className="size-4" />
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('roles.newRole') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      {role && copyOpen && (
        <CopyRoleDialog open={copyOpen} onOpenChange={setCopyOpen} role={role} />
      )}
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function RoleEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const {
    data: role,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['role', id],
    queryFn: () => RolesApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('roles.title'), href: '/admin/roles' },
    { label: isNew ? t('roles.newRole') : t('roles.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={2} columns={2} />
      </AppShell>
    )
  }

  return <RoleEditForm key={id} role={role ?? null} id={id!} isNew={isNew} />
}
